/**
 * Stage 1A — shared duplicate review (Product Master + PSB create).
 * Advisory list of near-duplicates; creating anyway needs a recorded reason.
 */
import React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

const REASON_LABELS = {
  name_match: 'Same name (ignoring case/spacing)',
  brand_size_match: 'Same brand and size',
  barcode_match: 'Same barcode',
};

export const DUPLICATE_REASON_MIN = 10;

function CandidateRow({ c, onOpen, onUse }) {
  return (
    <li className="flex items-center justify-between gap-2 rounded border border-amber-200 bg-white px-2.5 py-1.5"
      data-testid={`duplicate-candidate-${c.id}`}>
      <div className="min-w-0">
        <div className="text-xs font-medium text-slate-800 truncate">{c.brand ? `${c.brand} · ` : ''}{c.product_name}</div>
        <div className="text-[10px] text-amber-700">
          {REASON_LABELS[c.reason] || c.reason}{c.case_size ? ` · case of ${c.case_size}` : ''}
          {String(c.status || '').toLowerCase() === 'inactive' ? ' · inactive' : ''}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        {onOpen && (
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => onOpen(c)} data-testid={`duplicate-open-${c.id}`}>
            <ExternalLink className="h-3 w-3 mr-1" /> Open
          </Button>
        )}
        {onUse && (
          <Button type="button" size="sm" className="h-7 text-xs"
            onClick={() => onUse(c)} data-testid={`duplicate-use-${c.id}`}>
            Use this product
          </Button>
        )}
      </div>
    </li>
  );
}

export function DuplicateReviewPanel({ candidates, reason, onReasonChange, onOpen, onUse, useHint }) {
  if (!candidates || candidates.length === 0) return null;
  const short = (reason || '').trim().length < DUPLICATE_REASON_MIN;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 space-y-2.5" data-testid="duplicate-review-panel">
      <div className="flex items-start gap-2 text-xs text-amber-900">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
        <span><strong>This may already exist.</strong> Open or use one of these, or record why this is a different product.</span>
      </div>
      <ul className="space-y-1.5">
        {candidates.map(c => <CandidateRow key={c.id} c={c} onOpen={onOpen} onUse={onUse} />)}
      </ul>
      {useHint && <p className="text-[11px] text-amber-800" data-testid="duplicate-use-hint">{useHint}</p>}
      <div className="space-y-1">
        <label className="text-[11px] font-semibold text-amber-900" htmlFor="duplicate-override-reason">
          Not a duplicate — reason <span className="text-red-600">*</span>
        </label>
        <textarea id="duplicate-override-reason" rows={2} maxLength={500}
          value={reason || ''}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. different pack size sold separately; supplier confirms a separate SKU"
          className="w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-xs resize-none"
          data-testid="duplicate-override-reason" />
        <p className={`text-[10px] ${short ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="duplicate-override-reason-hint">
          {short ? `At least ${DUPLICATE_REASON_MIN} characters. The reason is stored with the new product.` : 'Reason will be stored with the new product.'}
        </p>
      </div>
    </div>
  );
}
